export interface PaymentInfo {
  isPaid: boolean;
  amount: number;
}

export interface PaymentData {
  codAgendamento?: number | null;
  amount: number;
  status: string;
}

/**
 * Função que obtem os valores, formatados, dos pagamentos pendentes e processados de um atendimento do paciente (Reais).
 *
 * @param pagar - Indica se há um valor a ser considerado para o pagamento
 * @param valor - Valor a ser considerado para o pagamento
 * @param pago - Indica se o o valor considerado foi pago ou não
 * @param onlinePayments - Fragmento com as informações dos pagamentos online
 * @param relatedAppointmentsOnlinePayments - Fragmento com as informações dos pagamentos online de agendamentos relacionados
 * @returns Valores pagos e pendentes relacionados ao atendimento do paciente (Reais)
 */
export const getPaymentsPaidAndPendingAmounts = (
  pagar: boolean,
  valor: number,
  pago: boolean,
  onlinePayments: PaymentData[],
  relatedAppointmentsOnlinePayments: PaymentData[],
): {
  totalPaid: number;
  totalPending: number;
} => {
  const payments: PaymentInfo[] = [];

  payments.push(
    ...getOnlinePaymentsInfo(onlinePayments, relatedAppointmentsOnlinePayments),
  );

  if (pagar) {
    payments.push({ amount: valor, isPaid: pago });
  }

  const { totalPaid, totalPending } = payments.reduce(
    (acc, payment) => {
      if (payment.isPaid) {
        acc.totalPaid += payment.amount;
      } else {
        acc.totalPending += payment.amount;
      }
      return acc;
    },
    { totalPaid: 0, totalPending: 0 },
  );

  return { totalPaid, totalPending };
};

const getOnlinePaymentsInfo = (
  onlinePayments: PaymentData[],
  relatedAppointmentsOnlinePayments: PaymentData[],
): PaymentInfo[] => {
  if (onlinePayments.length === 0 && relatedAppointmentsOnlinePayments.length === 0)
    return [];

  const relatedAppointmentsSet = new Set(
    relatedAppointmentsOnlinePayments.map((item) => item.codAgendamento),
  );

  const combinedOnlinePayments = [
    ...onlinePayments.filter(
      (payment) => !relatedAppointmentsSet.has(payment.codAgendamento),
    ),
    ...relatedAppointmentsOnlinePayments,
  ];

  return combinedOnlinePayments.map((payment) => ({
    amount: payment.amount / 100,
    isPaid: payment.status === "paid",
  }));
};
